import {
  Award,
  BadgePlus,
  BookOpenText,
  Building2,
  CalendarClock,
  FileQuestion,
  GalleryVerticalEnd,
  Handshake,
  Images,
  LayoutGrid,
  LayoutTemplate,
  MapPinned,
  MessageSquareQuote,
  ScrollText,
  Search,
  SeparatorHorizontal,
  SquareChartGantt,
  Tags,
  Users,
  Video,
} from 'lucide-react';

type BlockIcon = typeof LayoutTemplate;

interface BlockSection {
  key: string;
  title: string;
  fields: string[];
  defaultOpen?: boolean;
}

export interface EditorBlockMeta {
  icon: BlockIcon;
  description: string;
  sections: BlockSection[];
  primaryFields?: string[];
  fieldCompanions?: Record<string, string[]>;
  summary: (props: Record<string, unknown>) => string;
}

function firstText(props: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = props[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function countOf(value: unknown) {
  return Array.isArray(value) ? value.length : 0;
}

export const editorBlockMeta: Record<string, EditorBlockMeta> = {
  AppointmentBooking: {
    icon: CalendarClock,
    description: 'Permite a los visitantes reservar una cita eligiendo servicio, profesional y horario.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'subtitle'] },
      { key: 'confirmation', title: 'Confirmación', fields: ['successTitle', 'successText'] },
    ],
    primaryFields: ['title', 'subtitle', 'eyebrow'],
    fieldCompanions: {
      title: ['subtitle'],
      successTitle: ['successText'],
    },
    summary: (props) => firstText(props, ['title', 'eyebrow']) || 'Reservas online',
  },
  Hero: {
    icon: LayoutTemplate,
    description: 'Portada destacada con título grande, texto de bienvenida, botones e imagen de fondo.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['heroStyle', 'eyebrow', 'script', 'headline', 'headlineSize', 'headlineLight', 'subheadline', 'subheadlineSize'] },
      { key: 'actions', title: 'Botones y enlaces', fields: ['ctaLabel', 'ctaHref', 'ctaSecondaryLabel', 'ctaSecondaryHref'] },
      { key: 'media', title: 'Imagen', fields: ['backgroundImage', 'overlay'] },
      { key: 'stats', title: 'Estadísticas', fields: ['stats'] },
    ],
    primaryFields: ['headline', 'subheadline', 'eyebrow', 'backgroundImage', 'ctaLabel'],
    fieldCompanions: {
      headline: ['headlineSize'],
      subheadline: ['subheadlineSize'],
      backgroundImage: ['overlay'],
      ctaLabel: ['ctaHref'],
      ctaSecondaryLabel: ['ctaSecondaryHref'],
    },
    summary: (props) => firstText(props, ['headline', 'subheadline', 'eyebrow']) || 'Hero principal',
  },
  PropertiesGrid: {
    icon: Building2,
    description: 'Muestra una cuadrícula con tus propiedades, con filtros y un botón para ver más.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'title1', 'title2', 'titleScript', 'badge', 'subtitle'] },
      { key: 'listing', title: 'Listado', fields: ['limit', 'featured', 'status', 'featuredLayout', 'columns', 'showFilters'] },
      { key: 'action', title: 'Botón', fields: ['showCta', 'ctaLabel', 'ctaHref'] },
    ],
    primaryFields: ['title', 'subtitle', 'eyebrow'],
    fieldCompanions: {
      title: ['subtitle'],
      title1: ['title2'],
      ctaLabel: ['ctaHref'],
    },
    summary: (props) => firstText(props, ['title', 'title1', 'subtitle']) || 'Listado de propiedades',
  },
  Agents: {
    icon: Users,
    description: 'Presenta a las personas de tu equipo con su foto y datos de contacto.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'titleScript', 'subtitle'] },
      { key: 'layout', title: 'Listado', fields: ['featured', 'columns'] },
    ],
    primaryFields: ['title', 'subtitle', 'eyebrow'],
    fieldCompanions: {
      title: ['subtitle'],
    },
    summary: (props) => firstText(props, ['title', 'subtitle']) || 'Equipo de agentes',
  },
  TextSection: {
    icon: ScrollText,
    description: 'Un bloque de texto con título e imagen para contar tu historia o explicar un servicio.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'titleSize', 'titleScript', 'body', 'body2'] },
      { key: 'media', title: 'Imagen', fields: ['image', 'imageAlt', 'imagePosition'] },
      { key: 'action', title: 'Botón y destacado', fields: ['badgeValue', 'badgeLabel', 'ctaLabel', 'ctaHref'] },
      { key: 'design', title: 'Diseño', fields: ['design'] },
    ],
    primaryFields: ['title', 'body', 'eyebrow', 'image'],
    fieldCompanions: {
      title: ['titleSize'],
      image: ['imageAlt', 'imagePosition'],
      ctaLabel: ['ctaHref'],
    },
    summary: (props) => firstText(props, ['title', 'eyebrow']) || 'Sección de texto',
  },
  Stats: {
    icon: SquareChartGantt,
    description: 'Resalta cifras o pasos clave de tu negocio en una fila de datos destacados.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['title', 'title1', 'title2', 'variant'] },
      { key: 'stats', title: 'Items', fields: ['steps', 'stats'] },
    ],
    primaryFields: ['title', 'title1', 'title2'],
    fieldCompanions: {
      title1: ['title2'],
    },
    summary: (props) => {
      const title = firstText(props, ['title', 'title1']);
      const variant = typeof props.variant === 'string' ? props.variant : 'proceso';
      return title ? `${title} · ${variant}` : `Bloque ${variant}`;
    },
  },
  Contact: {
    icon: Handshake,
    description: 'Sección de contacto con formulario y mapa para que te escriban o te encuentren.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['title', 'titleScript', 'subtitle'] },
      { key: 'options', title: 'Opciones', fields: ['showForm', 'showMap', 'mapEmbedUrl'] },
    ],
    primaryFields: ['title', 'subtitle'],
    fieldCompanions: {
      title: ['subtitle'],
    },
    summary: (props) => firstText(props, ['title', 'subtitle']) || 'Contacto',
  },
  CTA: {
    icon: BadgePlus,
    description: 'Una llamada a la acción con título y botones para invitar a dar el siguiente paso.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'headline', 'headlineSize', 'headlineScript', 'body'] },
      { key: 'actions', title: 'Botones', fields: ['ctaLabel', 'ctaHref', 'ctaSecondaryLabel', 'ctaSecondaryHref'] },
      { key: 'contact', title: 'Información extra', fields: ['showContact'] },
    ],
    primaryFields: ['headline', 'body', 'eyebrow', 'ctaLabel'],
    fieldCompanions: {
      headline: ['headlineSize'],
      ctaLabel: ['ctaHref'],
      ctaSecondaryLabel: ['ctaSecondaryHref'],
    },
    summary: (props) => firstText(props, ['headline', 'eyebrow']) || 'Llamada a la acción',
  },
  Blog: {
    icon: BookOpenText,
    description: 'Lista tus últimas entradas del blog con un botón para ver todas.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'titleScript'] },
      { key: 'listing', title: 'Listado', fields: ['limit', 'columns', 'showCta'] },
      { key: 'action', title: 'Botón', fields: ['ctaLabel', 'ctaHref'] },
    ],
    primaryFields: ['title', 'eyebrow', 'ctaLabel'],
    fieldCompanions: {
      ctaLabel: ['ctaHref'],
    },
    summary: (props) => firstText(props, ['title', 'eyebrow']) || 'Entradas del blog',
  },
  Testimonials: {
    icon: MessageSquareQuote,
    description: 'Comparte opiniones y reseñas de tus clientes para generar confianza.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'headline', 'headlineScript'] },
      { key: 'items', title: 'Testimonios', fields: ['testimonials'] },
    ],
    primaryFields: ['headline', 'eyebrow'],
    fieldCompanions: {
      headline: ['eyebrow'],
    },
    summary: (props) => {
      const title = firstText(props, ['headline', 'eyebrow']);
      const total = countOf(props.testimonials);
      return title ? `${title} · ${total} testimonios` : `${total} testimonios`;
    },
  },
  StickyScroll: {
    icon: GalleryVerticalEnd,
    description: 'Cuenta tu proceso paso a paso con una imagen que acompaña mientras se desplaza.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['ruleLabel', 'ruleRight', 'steps'] },
      { key: 'media', title: 'Imagen', fields: ['image', 'imageAlt', 'imageCaption'] },
      { key: 'action', title: 'Botón', fields: ['ctaLabel', 'ctaHref'] },
    ],
    primaryFields: ['ruleLabel', 'ruleRight', 'image', 'ctaLabel'],
    fieldCompanions: {
      ruleLabel: ['ruleRight'],
      image: ['imageAlt'],
      ctaLabel: ['ctaHref'],
    },
    summary: (props) => {
      const total = countOf(props.steps);
      const label = firstText(props, ['ruleLabel', 'ruleRight']);
      return label ? `${label} · ${total} pasos` : `${total} pasos narrativos`;
    },
  },
  FAQ: {
    icon: FileQuestion,
    description: 'Responde las preguntas más frecuentes en una lista desplegable.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title'] },
      { key: 'items', title: 'Preguntas', fields: ['items'] },
    ],
    primaryFields: ['title', 'eyebrow'],
    fieldCompanions: {
      title: ['eyebrow'],
    },
    summary: (props) => {
      const title = firstText(props, ['title', 'eyebrow']);
      const total = countOf(props.items);
      return title ? `${title} · ${total} preguntas` : `${total} preguntas frecuentes`;
    },
  },
  Offices: {
    icon: MapPinned,
    description: 'Muestra tus sucursales o ubicaciones con su dirección y mapa.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'titleScript', 'subtitle'] },
      { key: 'layout', title: 'Listado', fields: ['showMap', 'columns'] },
    ],
    primaryFields: ['title', 'subtitle', 'eyebrow'],
    fieldCompanions: {
      title: ['subtitle'],
    },
    summary: (props) => firstText(props, ['title', 'subtitle']) || 'Sucursales',
  },
  Gallery: {
    icon: Images,
    description: 'Muestra una colección de imágenes en cuadrícula, mosaico o carrusel.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'titleScript', 'titleSize'] },
      { key: 'layout', title: 'Distribución', fields: ['layout', 'columns'] },
      { key: 'items', title: 'Imágenes', fields: ['items'] },
    ],
    primaryFields: ['title', 'eyebrow'],
    fieldCompanions: { title: ['titleSize'], layout: ['columns'] },
    summary: (props) => {
      const title = firstText(props, ['title', 'eyebrow']);
      const total = countOf(props.items);
      return title ? `${title} · ${total} imágenes` : `${total} imágenes`;
    },
  },
  LogoStrip: {
    icon: Award,
    description: 'Una tira con los logos de marcas o clientes que confían en ti.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'grayscale'] },
      { key: 'items', title: 'Logos', fields: ['items'] },
    ],
    primaryFields: ['title', 'eyebrow'],
    summary: (props) => {
      const title = firstText(props, ['title', 'eyebrow']);
      const total = countOf(props.items);
      return title ? `${title} · ${total} logos` : `${total} logos`;
    },
  },
  Pricing: {
    icon: Tags,
    description: 'Compara tus planes o paquetes con precios, beneficios y un botón por plan.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'titleScript', 'titleSize', 'subtitle'] },
      { key: 'plans', title: 'Planes', fields: ['plans'] },
    ],
    primaryFields: ['title', 'subtitle', 'eyebrow'],
    fieldCompanions: { title: ['titleSize', 'subtitle'] },
    summary: (props) => {
      const title = firstText(props, ['title', 'eyebrow']);
      const total = countOf(props.plans);
      return title ? `${title} · ${total} planes` : `${total} planes`;
    },
  },
  Features: {
    icon: LayoutGrid,
    description: 'Una cuadrícula de servicios o ventajas, cada uno con su ícono y descripción.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'titleScript', 'titleSize', 'subtitle'] },
      { key: 'layout', title: 'Distribución', fields: ['columns'] },
      { key: 'items', title: 'Elementos', fields: ['items'] },
    ],
    primaryFields: ['title', 'subtitle', 'eyebrow'],
    fieldCompanions: { title: ['titleSize', 'subtitle'] },
    summary: (props) => {
      const title = firstText(props, ['title', 'eyebrow']);
      const total = countOf(props.items);
      return title ? `${title} · ${total} elementos` : `${total} elementos`;
    },
  },
  Video: {
    icon: Video,
    description: 'Incrusta un video de YouTube o Vimeo que solo carga al hacer clic.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['eyebrow', 'title', 'titleSize'] },
      { key: 'video', title: 'Video', fields: ['provider', 'videoId', 'poster', 'caption'] },
    ],
    primaryFields: ['title', 'videoId', 'eyebrow'],
    fieldCompanions: { provider: ['videoId'] },
    summary: (props) => firstText(props, ['title', 'eyebrow']) || 'Video',
  },
  Divider: {
    icon: SeparatorHorizontal,
    description: 'Un separador o espacio en blanco para dar respiro entre secciones.',
    sections: [
      { key: 'content', title: 'Contenido', defaultOpen: true, fields: ['variant', 'height'] },
    ],
    primaryFields: ['variant'],
    summary: (props) => {
      const variant = typeof props.variant === 'string' ? props.variant : 'line';
      const map: Record<string, string> = { space: 'Solo espacio', line: 'Línea', ornament: 'Adorno central' };
      return map[variant] ?? 'Separador';
    },
  },
  Search: {
    icon: Search,
    description: 'Una barra de búsqueda para que los visitantes filtren tus propiedades.',
    sections: [
      { key: 'content', title: 'Opciones', defaultOpen: true, fields: ['href', 'overlap'] },
    ],
    primaryFields: ['href'],
    summary: () => 'Buscador de propiedades',
  },
};
